import { useEffect, useMemo, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { reviewsApi, type ReviewResponse } from "@/lib/api";

/* ── Reviews Component ───────────────────────────────────────────
   BACKEND ENDPOINTS:
   GET  /api/v1/reviews?venueId=:id → reviewsApi.list() (load reviews)
   POST /api/v1/venues/:id/reviews  → reviewsApi.create() (submit review)
   ─────────────────────────────────────────────────────────────── */

export function Reviews({ venueId }: { venueId: string; venueRating: number }) {
  const { isAuthenticated, role } = useAuth();
  // Only guests (accounts with no admin/business role) may post reviews. This
  // stops venue owners / super admins from fabricating reviews on listings.
  const canReview = !role;
  const [list, setList] = useState<ReviewResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    reviewsApi
      .list({ venueId, isApproved: true, pageSize: 50 })
      .then((res) => setList(res.data))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [venueId]);

  const breakdown = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    list.forEach((r) => { counts[r.rating - 1]++; });
    return counts;
  }, [list]);
  const total = list.length;
  const avg = total ? list.reduce((s, r) => s + r.rating, 0) / total : null;

  const submit = async () => {
    if (!isAuthenticated) { toast.error("Sign in to leave a review"); return; }
    if (!canReview) { toast.error("Only guests can leave reviews"); return; }
    if (!draft.comment.trim()) { toast.error("Add a review comment"); return; }
    setSubmitting(true);
    try {
      const newReview = await reviewsApi.create(venueId, {
        rating: draft.rating,
        comment: draft.comment.trim(),
      });
      setList((prev) => [newReview, ...prev]);
      setDraft({ rating: 5, comment: "" });
      setOpen(false);
      toast.success("Thanks - your review was posted.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to post review.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Guest reviews</h2>
          <p className="text-sm text-muted-foreground">{total} verified stays</p>
        </div>
        {canReview && (
          <Button variant="outline" onClick={() => setOpen((o) => !o)}>{open ? "Cancel" : "Write a review"}</Button>
        )}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="lg:sticky lg:top-32 h-fit rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
          <div className="flex items-baseline gap-2">
            {avg === null ? (
              <>
                <span className="font-display text-4xl font-bold">New</span>
                <span className="text-sm text-muted-foreground">No reviews yet</span>
              </>
            ) : (
              <>
                <span className="font-display text-5xl font-bold">{avg.toFixed(1)}</span>
                <span className="text-sm text-muted-foreground">/ 5</span>
              </>
            )}
          </div>
          <div className="mt-1 flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`size-4 ${avg !== null && i <= Math.round(avg) ? "fill-gold text-gold" : "text-muted-foreground/30"}`} />
            ))}
          </div>
          <ul className="mt-5 space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const c = breakdown[star - 1];
              const pct = total ? (c / total) * 100 : 0;
              return (
                <li key={star} className="flex items-center gap-2 text-xs">
                  <span className="w-3 text-muted-foreground">{star}</span>
                  <Star className="size-3 fill-gold text-gold" />
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-right tabular-nums text-muted-foreground">{c}</span>
                </li>
              );
            })}
          </ul>
        </aside>

        <div className="space-y-4">
          {open && canReview && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <h3 className="font-display text-lg font-semibold">Share your experience</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="mb-2 block">Rating</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button key={i} type="button" onClick={() => setDraft({ ...draft, rating: i })} aria-label={`${i} star`}>
                        <Star className={`size-7 ${i <= draft.rating ? "fill-gold text-gold" : "text-muted-foreground/40"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rb">Your review</Label>
                  <Textarea id="rb" rows={4} value={draft.comment} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft({ ...draft, comment: e.target.value })} placeholder="Tell future guests what made your stay special..." />
                </div>
                <Button onClick={submit} disabled={submitting}>
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : "Post review"}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No reviews yet — be the first to leave one.
            </div>
          ) : (
            list.map((r) => (
              <article key={r.id} className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
                <header className="flex items-start gap-3">
                  <span className="grid size-10 place-items-center rounded-full bg-primary/10 font-semibold text-primary">{r.venueName.slice(0, 2).toUpperCase()}</span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">Guest</span>
                      <span className="text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString("en-NG", { year: "numeric", month: "short", day: "numeric" })}</span>
                    </div>
                    <div className="mt-0.5 flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`size-3.5 ${i <= r.rating ? "fill-gold text-gold" : "text-muted-foreground/30"}`} />
                      ))}
                    </div>
                  </div>
                </header>
                <p className="mt-3 text-sm text-foreground/80">{r.comment}</p>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

